import React from 'react';
import { Result, Button, Typography, Alert, Card, Space } from 'antd';
import { Bug, RotateCw, Home, AlertTriangle } from 'lucide-react';

import axiosInstance from 'utils/axios';
import { devLogApiError } from 'utils/devLogApiError';
import DeployUpdateScreen from 'components/Error/DeployUpdateScreen';

const { Paragraph, Text, Title: TypographyTitle } = Typography;

// ✅ Rate limiting для отправки ошибок - не более 1 ошибки в 10 секунд
const errorReportQueue = {
  lastReportTime: 0,
  reportInterval: 10000, // 10 секунд
  pendingErrors: [],
  isProcessing: false
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      reportStatus: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorId = 'ERR_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    const isChunkLoadError = error?.name === 'ChunkLoadError' ||
      (error?.message && (
        String(error.message).includes('Loading chunk') ||
        String(error.message).includes('Loading CSS chunk')
      ));

    this.setState({
      error,
      errorInfo,
      errorId
    });

    this.logError(error, errorInfo, errorId);

    // ChunkLoadError — не отправляем на сервер (решение: обновить страницу, не спамить 429)
    if (!isChunkLoadError) {
      this.sendErrorToServer(error, errorInfo, errorId);
    }
  }

  logError = (error, errorInfo, errorId) => {
    devLogApiError(`React: граница ошибок [${errorId}]`, error);
    if (process.env.NODE_ENV === 'development' && errorInfo?.componentStack) {
      // eslint-disable-next-line no-console
      console.warn('[граница ошибок: componentStack]', errorInfo.componentStack);
    }
  };

  sendErrorToServer = async (error, errorInfo, errorId) => {
    // ✅ КРИТИЧНО: Отправка ошибок должна быть полностью асинхронной и не блокировать работу приложения
    // ✅ Rate limiting: не более 1 ошибки в 10 секунд, остальные добавляются в очередь

    const now = Date.now();
    const timeSinceLastReport = now - errorReportQueue.lastReportTime;

    // Если прошло достаточно времени - отправляем сразу
    if (timeSinceLastReport >= errorReportQueue.reportInterval && !errorReportQueue.isProcessing) {
      errorReportQueue.lastReportTime = now;
      errorReportQueue.isProcessing = true;
      this._sendErrorRequest(error, errorInfo, errorId);
    } else {
      // Добавляем в очередь, но ограничиваем размер очереди (максимум 5 ошибок)
      if (errorReportQueue.pendingErrors.length < 5) {
        errorReportQueue.pendingErrors.push({ error, errorInfo, errorId });
      }
      // Планируем обработку очереди, если она еще не обрабатывается
      if (!errorReportQueue.isProcessing) {
        const delay = errorReportQueue.reportInterval - timeSinceLastReport;
        setTimeout(() => this._processErrorQueue(), delay);
      }
    }
  };

  _processErrorQueue = async () => {
    if (errorReportQueue.pendingErrors.length === 0) {
      errorReportQueue.isProcessing = false;
      return;
    }

    const { error, errorInfo, errorId } = errorReportQueue.pendingErrors.shift();
    errorReportQueue.lastReportTime = Date.now();
    errorReportQueue.isProcessing = true;

    await this._sendErrorRequest(error, errorInfo, errorId);

    // Планируем следующую обработку, если есть еще ошибки
    if (errorReportQueue.pendingErrors.length > 0) {
      setTimeout(() => this._processErrorQueue(), errorReportQueue.reportInterval);
    } else {
      errorReportQueue.isProcessing = false;
    }
  };

  _sendErrorRequest = async (error, errorInfo, errorId) => {
    // Используем setTimeout для отправки в следующем тике event loop, чтобы не блокировать UI
    setTimeout(() => {
      // Используем async IIFE для выполнения асинхронного кода
      (async () => {
        try {
          const errorData = {
            errorId,
            error: {
              message: error?.message || 'Unknown error',
              stack: error?.stack || 'N/A'
            },
            errorInfo: {
              componentStack: errorInfo?.componentStack || 'N/A'
            },
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent || 'Unknown',
            url: window.location.href || 'Unknown'
          };

          // Отправляем на сервер через публичный endpoint (доступен всем авторизованным пользователям)
          // Используем axios с коротким таймаутом, чтобы не блокировать работу
          try {
            await axiosInstance.post('/api/auth/report-frontend-error', errorData, {
              timeout: 3000 // Короткий таймаут 3 секунды, чтобы не блокировать UI
            });
            console.log('✅ Ошибка автоматически отправлена на сервер:', errorId);
          } catch (err) {
            // Игнорируем все ошибки отправки - это не критично для работы приложения
            if (err.response?.status === 401) {
              console.log('⚠️ Отправка ошибки запрещена (не авторизован)');
            } else if (err.response?.status === 429) {
              console.warn('⚠️ Превышен лимит отправки ошибок (rate limit), пропускаем');
            } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
              console.warn('⚠️ Таймаут при отправке ошибки (не критично)');
            } else {
              console.warn('⚠️ Не удалось отправить ошибку на сервер (не критично):', err.message);
            }
          }
        } catch (err) {
          // Игнорируем любые ошибки при попытке отправить ошибку - не должно влиять на пользователя
          console.warn('⚠️ Ошибка при попытке отправить отчет об ошибке (игнорируется):', err.message);
        }
      })().catch(() => {
        // Защита от любых необработанных ошибок - не должно влиять на пользователя
      }).finally(() => {
        errorReportQueue.isProcessing = false;
      });
    }, 0);
  };

  getUserId = () => {
    try {
      const userPayload = axiosInstance.getCurrentUser ? axiosInstance.getCurrentUser() : null;
      if (userPayload) {
        return userPayload.userId || userPayload.sub || userPayload.id || 'anonymous';
      }
    } catch (e) {
      return 'anonymous';
    }
    return 'anonymous';
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      reportStatus: null
    });
  };

  reportBug = async () => {
    const { error, errorInfo, errorId } = this.state;
    this.setState({
      reportStatus: {
        type: 'info',
        message: 'Отправляем отчёт об ошибке...'
      }
    });

    const bugReport = {
      errorId,
      error: {
        message: error?.message,
        stack: error?.stack
      },
      errorInfo: {
        componentStack: errorInfo?.componentStack
      },
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    try {
      // Отправляем отчет на сервер через публичный endpoint
      try {
        const response = await axiosInstance.post('/api/auth/report-frontend-error', bugReport);

        if (response.data.success) {
          this.setState({
            reportStatus: {
              type: 'success',
              message: `Отчёт об ошибке отправлен. ID ошибки: ${errorId}.`
            }
          });
          return;
        }
      } catch (err) {
        // Игнорируем 401 - не авторизован
        if (err.response?.status === 401) {
          console.log('⚠️ Отправка ошибки запрещена (не авторизован)');
        } else {
          console.warn('⚠️ Не удалось отправить отчет об ошибке:', err.message);
        }
      }

      // Копируем в буфер обмена как запасной вариант
      try {
        await navigator.clipboard?.writeText(JSON.stringify(bugReport, null, 2));
        this.setState({
          reportStatus: {
            type: 'warning',
            message: `Автоматическая отправка недоступна. Отчёт скопирован в буфер обмена. ID ошибки: ${errorId}.`
          }
        });
      } catch (clipErr) {
        this.setState({
          reportStatus: {
            type: 'error',
            message: `Не удалось отправить отчёт автоматически. Сообщите администратору ID ошибки: ${errorId}.`
          }
        });
      }
    } catch (err) {
      devLogApiError('граница ошибок: ручная отправка отчёта', err);
      this.setState({
        reportStatus: {
          type: 'error',
          message: `Ошибка при отправке отчёта. ID ошибки: ${errorId}.`
        }
      });
    }
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorId, reportStatus } = this.state;
      const { fallback, level = 'page' } = this.props;

      // Если передан кастомный fallback компонент
      if (fallback) {
        return fallback(error, errorInfo, this.handleRetry);
      }

      // Разные уровни отображения ошибок
      if (level === 'inline') {
        return (
          <Alert
            message="Ошибка компонента"
            description="Произошла ошибка при загрузке этого компонента"
            type="error"
            showIcon
            action={
              <Button size="small" onClick={this.handleRetry}>
                Повторить
              </Button>
            }
            style={{ margin: '16px 0' }}
          />
        );
      }

      if (level === 'section') {
        return (
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <AlertTriangle size={48} style={{ color: 'var(--err)', marginBottom: '16px' }} aria-hidden />
            <TypographyTitle level={4}>Что-то пошло не так</TypographyTitle>
            <Paragraph type="secondary">
              Произошла ошибка в этой части приложения
            </Paragraph>
            <Space>
              <Button onClick={this.handleRetry}>
                Повторить попытку
              </Button>
              <Button type="primary" onClick={this.handleReload}>
                Обновить страницу
              </Button>
            </Space>
          </div>
        );
      }

      // ChunkLoadError — после деплоя у пользователя закэширован старый main.js, чанки (JS/CSS) не найдены
      const isChunkLoadError = error?.name === 'ChunkLoadError' ||
        (error?.message && (
          String(error.message).includes('Loading chunk') ||
          String(error.message).includes('Loading CSS chunk')
        ));
      if (isChunkLoadError) {
        return (
          <DeployUpdateScreen
            error={error}
            reset={this.handleRetry}
          />
        );
      }

      // Полноэкранная ошибка (по умолчанию)
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--bg-soft)',
          padding: '24px'
        }}>
          <Card style={{ maxWidth: '600px', width: '100%' }}>
            <Result
              status="error"
              title="Упс! Что-то пошло не так"
              subTitle={
                <div>
                  <Paragraph>
                    Произошла непредвиденная ошибка. Наша команда уже уведомлена об этой проблеме.
                  </Paragraph>
                  <Paragraph>
                    Технические детали скрыты. Используйте ID ошибки для обращения в поддержку.
                  </Paragraph>
                  {errorId && (
                    <Paragraph>
                      <Text strong>ID ошибки:</Text> <Text code>{errorId}</Text>
                    </Paragraph>
                  )}
                </div>
              }
              extra={
                <Space direction="vertical" size="middle">
                  <Space>
                    <Button
                      type="primary"
                      icon={<RotateCw size={16} aria-hidden />}
                      onClick={this.handleReload}
                    >
                      Обновить страницу
                    </Button>
                    <Button
                      icon={<Home size={16} aria-hidden />}
                      onClick={this.handleGoHome}
                    >
                      На главную
                    </Button>
                    <Button
                      onClick={this.handleRetry}
                    >
                      Повторить попытку
                    </Button>
                  </Space>

                  <Button
                    type="link"
                    icon={<Bug size={16} aria-hidden />}
                    onClick={this.reportBug}
                    size="small"
                  >
                    Сообщить об ошибке
                  </Button>
                </Space>
              }
            />

            {reportStatus && (
              <Alert
                message={reportStatus.message}
                type={reportStatus.type}
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}

            {process.env.NODE_ENV === 'development' && error && (
              <Card
                title="Детали ошибки (только в режиме разработки)"
                size="small"
                style={{ marginTop: '16px' }}
              >
                <Paragraph>
                  <Text strong>Ошибка:</Text>
                  <br />
                  <Text code>{error.message}</Text>
                </Paragraph>

                {error.stack && (
                  <Paragraph>
                    <Text strong>Stack trace:</Text>
                    <br />
                    <Text code style={{ whiteSpace: 'pre-wrap', fontSize: '11px' }}>
                      {error.stack}
                    </Text>
                  </Paragraph>
                )}

                {errorInfo && errorInfo.componentStack && (
                  <Paragraph>
                    <Text strong>Component stack:</Text>
                    <br />
                    <Text code style={{ whiteSpace: 'pre-wrap', fontSize: '11px' }}>
                      {errorInfo.componentStack}
                    </Text>
                  </Paragraph>
                )}
              </Card>
            )}
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC для обертывания компонентов
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  return function WrappedComponent(props) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
};

// Хук для ручного вызова ошибки
export const useErrorHandler = () => {
  return (error, errorInfo) => {
    throw new Error(error);
  };
};

export default ErrorBoundary;